import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
export function NewsletterEditor() {
  const {
    currentWorkspace,
    currentSubRegion,
    getUnassignedForms,
    createNewsletter
  } = useWorkspace();
  const navigate = useNavigate();
  const availableForms = getUnassignedForms();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    formId: undefined as number | undefined,
    status: 'active' as const
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createNewsletter({
      workspaceId: currentWorkspace.id,
      subRegion: currentSubRegion || undefined,
      name: formData.name,
      description: formData.description,
      formId: formData.formId,
      status: formData.status
    });
    navigate('/newsletters');
  };
  return <div className="space-y-6 max-w-3xl" data-id="element-1431">
      <div className="flex items-center gap-4" data-id="element-1432">
        <Link to="/newsletters" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-1433">
          <ArrowLeft className="h-5 w-5" data-id="element-1434" />
        </Link>
        <div data-id="element-1435">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1436">
            Create Newsletter
          </h1>
          <p className="text-sm text-gray-500" data-id="element-1437">
            Set up a new audience list for campaigns.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6" data-id="element-1438">
        <div data-id="element-1439">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1440">
            Newsletter Name
          </label>
          <input type="text" required placeholder="e.g., General Updates, Weekly Newsletter" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} data-id="element-1441" />
        </div>

        <div data-id="element-1442">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1443">
            Description
          </label>
          <textarea rows={3} placeholder="Brief description of this newsletter's purpose..." className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.description} onChange={e => setFormData({
          ...formData,
          description: e.target.value
        })} data-id="element-1444" />
        </div>

        <div data-id="element-1445">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1446">
            Associated Signup Form (Optional)
          </label>
          {availableForms.length === 0 ? <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 flex items-start gap-2" data-id="element-1447">
              <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-1448" />
              <div className="text-sm text-yellow-700" data-id="element-1449">
                All forms are already assigned to newsletters. Create a new form
                or leave this newsletter without a form.
              </div>
            </div> : <>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.formId || ''} onChange={e => setFormData({
            ...formData,
            formId: e.target.value ? parseInt(e.target.value) : undefined
          })} data-id="element-1450">
                <option value="" data-id="element-1451">No form linked</option>
                {availableForms.map(form => <option key={form.id} value={form.id} data-id="element-1452">
                    {form.name} {form.subRegion ? `(${form.subRegion})` : ''}
                  </option>)}
              </select>
              <p className="mt-1 text-xs text-gray-500" data-id="element-1453">
                Link a signup form to automatically add new subscribers to this
                newsletter. Each form can only be linked to one newsletter.
              </p>
            </>}
        </div>

        <div className="bg-gray-50 rounded-lg p-4 space-y-2" data-id="element-1454">
          <h3 className="text-sm font-medium text-gray-900" data-id="element-1455">Scope</h3>
          <div className="text-sm text-gray-600" data-id="element-1456">
            <div data-id="element-1457">
              <strong data-id="element-1458">Team:</strong> {currentWorkspace.name}
            </div>
            <div data-id="element-1459">
              <strong data-id="element-1460">Sub-Region:</strong>{' '}
              {currentSubRegion || 'All Regions (Team-wide)'}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2" data-id="element-1461">
            This newsletter will be available for campaigns targeting this
            scope.
          </p>
        </div>

        <div data-id="element-1462">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1463">
            Status
          </label>
          <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.status} onChange={e => setFormData({
          ...formData,
          status: e.target.value as 'active' | 'archived'
        })} data-id="element-1464">
            <option value="active" data-id="element-1465">Active</option>
            <option value="archived" data-id="element-1466">Archived</option>
          </select>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-1467">
          <Link to="/newsletters" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1468">
            Cancel
          </Link>
          <button type="submit" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-1469">
            <Save className="h-4 w-4" data-id="element-1470" />
            Create Newsletter
          </button>
        </div>
      </form>
    </div>;
}